/* Add new component CaseStudyNewHeader */

import React, { useState, useEffect, useRef } from 'react';
import style from './CaseStudyNewHeader.module.scss';
import { NewsDetailHeader } from '../NewsDetail';
import CountingNumbers from '../CountingNumbers';

const CaseStudyNewHeader = component => {
    // All hooks must be called at the top level
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Quote slider state
    const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
    const [autoPlay, setAutoPlay] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [dragDeltaX, setDragDeltaX] = useState(0);
    const startXRef = useRef(0);
    const sliderRef = useRef(null);

    // Load data on component mount
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                // Simulate API call delay
                await new Promise(resolve => setTimeout(resolve, 100));

                // Use provided component data
                setData(component);
            } catch (error) {
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [component]);

    // Auto-advance quotes every 5 seconds (Disable if user interacts)
    useEffect(() => {
        const quotes = data?.quotes || [];
        if (!autoPlay || quotes.length <= 1) return;
        const interval = setInterval(() => {
            setCurrentQuoteIndex(prevIndex => (prevIndex === quotes.length - 1 ? 0 : prevIndex + 1));
        }, 5000);
        return () => clearInterval(interval);
    }, [autoPlay, data?.quotes?.length]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!data) {
        return null;
    }

    // Destructure data like CaseStudyScrollHijack
    const {
        title,
        logo,
        headerImage,
        sector,
        origin,
        status,
        website,
        stats = [],
        quotes = [],
        breadcrumb,
        publishingDate,
        authorPhoto,
        authorName,
        share,
    } = data;

    // Setup variables like CaseStudyScrollHijack
    const websiteLink = website?.link?.url
        ? website.link.url.indexOf('http') > -1
            ? website.link.url
            : 'http://' + website.link.url
        : null;

    // Handle dot click
    const handleDotClick = index => {
        setAutoPlay(false);
        setCurrentQuoteIndex(index);
        setDragDeltaX(0);
    };

    // Drag helpers
    const onDragStart = clientX => {
        setAutoPlay(false);
        setIsDragging(true);
        startXRef.current = clientX;
    };

    const onDragMove = clientX => {
        if (!isDragging) return;
        const delta = clientX - startXRef.current;
        setDragDeltaX(delta);
    };

    const onDragEnd = () => {
        if (!isDragging) return;
        // Use container width to set threshold ~15% (15% of the container width)
        const rect = sliderRef.current?.getBoundingClientRect();
        const width = rect?.width || 1;
        const threshold = width * 0.15;

        let nextIndex = currentQuoteIndex;
        if (Math.abs(dragDeltaX) > threshold) {
            if (dragDeltaX < 0 && currentQuoteIndex < quotes.length - 1) {
                nextIndex = currentQuoteIndex + 1;
            } else if (dragDeltaX > 0 && currentQuoteIndex > 0) {
                nextIndex = currentQuoteIndex - 1;
            }
        }
        setCurrentQuoteIndex(nextIndex);
        setIsDragging(false);
        setDragDeltaX(0);
    };

    // Mouse events
    const handleMouseDown = e => {
        e.preventDefault();
        onDragStart(e.clientX);
    };
    const handleMouseMove = e => {
        if (!isDragging) return;
        onDragMove(e.clientX);
    };
    const handleMouseUp = () => {
        onDragEnd();
    };
    const handleMouseLeave = () => {
        onDragEnd();
    };

    // Touch events
    const handleTouchStart = e => {
        const t = e.touches?.[0];
        if (!t) return;
        onDragStart(t.clientX);
    };
    const handleTouchMove = e => {
        const t = e.touches?.[0];
        if (!t) return;
        onDragMove(t.clientX);
    };
    const handleTouchEnd = () => {
        onDragEnd();
    };

    return (
        <div className={[style.caseStudyNewHeader, 'container'].join(' ')}>
            {/* News Detail Header */}
            {breadcrumb && publishingDate && (
                <NewsDetailHeader
                    header={{
                        date: publishingDate,
                        photo: authorPhoto ? { url: authorPhoto.url } : null,
                        name: authorName,
                        share: share || {
                            title: 'SHARE',
                            items: [
                                {
                                    title: 'twitter',
                                    icon: 'twitter2',
                                },
                                {
                                    title: 'linkedin',
                                    icon: 'linkedin2',
                                },
                            ],
                        },
                    }}
                    sharethis={share ? 'block' : 'block'}
                    currentUrl={typeof window !== 'undefined' ? window.location.href : ''}
                    content={{ headline: title }}
                />
            )}
            {/* Hero Banner Section with Header Image and Overlay */}
            <div className={style.heroSection}>
                {logo && (
                    <div className={style.topLogoSection}>
                        <img src={logo.url} alt={logo.alt} />
                    </div>
                )}
                {headerImage && (
                    <div className={style.headerImage}>
                        <img src={headerImage.url} alt={headerImage.alt} />
                    </div>
                )}
                <div className={style.overlaySection}>
                    <div className={style.overlayContent}>
                        <div className={style.titleSection}>
                            <h1 className={style.title}>{title}</h1>
                        </div>
                        <div className={style.statsSection}>
                            {stats.map((stat, index) => (
                                <div key={index} className={style.statItem}>
                                    <div
                                        className={style.statValue}
                                        dangerouslySetInnerHTML={{ __html: stat.description }}
                                    />
                                    <div className={style.statLabel}>
                                        {stat.disableCountingAnimation ? (
                                            <>{stat.title}</>
                                        ) : (
                                            <CountingNumbers value={stat.title} />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className={style.twoColumnLayout}>
                <div className={style.leftColumn}>
                    <div className={style.companyInfo}>
                        <div className={style.infoItem}>
                            <div className={style.infoIcon}>
                                <img src='/images/casestudy/ico.sector2.svg' alt='' />
                                <span className={style.infoLabel}>Sector</span>
                            </div>
                            <span className={style.infoValue}>{sector}</span>
                        </div>
                        <div className={style.infoItem}>
                            <div className={style.infoIcon}>
                                <img src='/images/casestudy/ico.countryregion2.svg' alt='' />
                                <span className={style.infoLabel}>Origin</span>
                            </div>
                            <span className={style.infoValue}>{origin}</span>
                        </div>
                        <div className={style.infoItem}>
                            <div className={style.infoIcon}>
                                <img src='/images/casestudy/ico.status2.svg' alt='' />
                                <span className={style.infoLabel}>Status</span>
                            </div>
                            <span className={style.infoValue}>{status}</span>
                        </div>
                        <div className={style.infoItem}>
                            <div className={style.infoIcon}>
                                <img src='/images/casestudy/ico.sector2.svg' alt='' />
                                <span className={style.infoLabel}>Website</span>
                            </div>
                            <a
                                href={websiteLink}
                                target={website.link?.target}
                                className={[style.websiteLink, style.infoValue].join(' ')}
                            >
                                {website.text || website.link?.url}
                            </a>
                        </div>
                    </div>
                </div>
                <div className={style.rightColumn}>
                    {quotes.length > 0 && (
                        <>
                            <img src='/images/casestudy/ico.quote2.svg' className={style.quoteIconTop} alt='' />
                            <img src='/images/casestudy/ico.quote2.svg' className={style.quoteIconBottom} alt='' />
                            <div className={style.testimonialSection}>
                                <div
                                    className={style.quoteSlider}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseLeave}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                    ref={sliderRef}
                                >
                                    <div
                                        className={style.quoteSlide}
                                        style={{
                                            transform: `translateX(calc(-${currentQuoteIndex *
                                                100}% + ${dragDeltaX}px))`,
                                            transition: isDragging ? 'none' : 'transform 0.5s ease',
                                        }}
                                    >
                                        {quotes.map((quote, index) => (
                                            <div key={index} className={style.quote}>
                                                <div className={style.quoteText}>{quote.quote}</div>
                                                <div className={style.quoteAuthor}>
                                                    {quote.quoteImage?.url && quote.quoteImage.url.trim() !== '' && (
                                                        <div className={style.thumbnail}>
                                                            <img
                                                                src={quote.quoteImage.url}
                                                                alt={quote.quoteImage.alt}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className={style.authorDetails}>
                                                        <a href={quote.authorLink} className={style.authorName}>
                                                            {quote.authorName}
                                                        </a>
                                                        <div className={style.authorRole}>{quote.authorRole}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {quotes.length > 1 && (
                                    <div className={style.quoteDots}>
                                        {quotes.map((_, index) => (
                                            <span
                                                key={index}
                                                className={`${style.dot} ${
                                                    index === currentQuoteIndex ? style.active : ''
                                                }`}
                                                onClick={() => handleDotClick(index)}
                                            ></span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CaseStudyNewHeader;
